import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useAnimations } from '../context/AnimationContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CollapsibleCard } from '../components/ui/collapsible-card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SkeletonProfile, SkeletonNavGrid, SkeletonCard, SkeletonLeaderboard } from '../components/ui/skeleton-card';
import { toast } from 'sonner';
import AnimatedXPCounter from '../components/AnimatedXPCounter';
import FramedAvatar from '../components/FramedAvatar';
import AnimatedLevelProgress from '../components/AnimatedLevelProgress';
import StoriesMoments from '../components/StoriesMoments';
import CrewProgressBanner from '../components/home/CrewProgressBanner';
import FlameIndicator from '../components/FlameIndicator';
import { titleForLevel } from '../utils/levelTitles';
import { AnimatedBadge, BadgeShowcase } from '../components/AnimatedBadge';
import { useDashboardPreferences, DASHBOARD_SECTIONS } from '../hooks/useDashboardPreferences';
import useDashboardData from '../hooks/useDashboardData';
import { LeaderboardPreview, OdysseyCard, DashboardHeader } from '../components/home';
import PushNotificationManager from '../components/PushNotificationManager';
import QuickActions from '../components/QuickActions';
import GlobalSearch from '../components/GlobalSearch';
// import MobileNav from '../components/MobileNav'; // Removed - using grid nav instead
import InstallPrompt from '../components/InstallPrompt';
import WelcomeBackRecap from '../components/WelcomeBackRecap';
import VigieCard from '../components/home/VigieCard';
import ChallengesCard from '../components/home/ChallengesCard';
import { useUnread } from '../context/UnreadContext';
import CalendarWidget from '../components/home/CalendarWidget';
import DailyChallengeCard from '../components/home/DailyChallengeCard';
import QuestionsSpotlightCard from '../components/home/QuestionsSpotlightCard';
import XPRewardsCard from '../components/home/XPRewardsCard';
import DraggableSection from '../components/DraggableSection';
import { renderWithMentions } from '../utils/mentionParser';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Anchor, Calendar, MessageSquare, Trophy, Lightbulb, Waves,
  FolderOpen, HelpCircle, Users, Hash, ChevronRight, Video, Bell,
  BookMarked,
  Compass, MapPin, Activity, Ship, Play, Target, Clock, ExternalLink, Search, Telescope, GripVertical, Settings2, User
} from 'lucide-react';

const NAV_ITEMS = [
  { icon: <Hash className="w-5 h-5" />, label: 'Channels', path: '/channels', color: 'text-blue-500', tourId: 'nav-channels' },
  { icon: <Target className="w-5 h-5" />, label: 'Defis', path: '/challenges', color: 'text-orange-500', tourId: 'nav-challenges' },
  { icon: <Ship className="w-5 h-5" />, label: 'Mon Odyssee', path: '/progression', color: 'text-cyan-500', showProgress: true },
  { icon: <BookMarked className="w-5 h-5" />, label: 'Mon Carnet', path: '/carnet', color: 'text-amber-600' },
  { icon: <MessageSquare className="w-5 h-5" />, label: 'Messages', path: '/dm', color: 'text-violet-500' },
  { icon: <Calendar className="w-5 h-5" />, label: 'Calendrier', path: '/calendar', color: 'text-red-400', tourId: 'nav-calendar' },
  { icon: <Play className="w-5 h-5" />, label: 'Replays', path: '/replays', color: 'text-red-500', tourId: 'nav-replays' },
  { icon: <HelpCircle className="w-5 h-5" />, label: 'Q&A', path: '/questions', color: 'text-pink-500', tourId: 'nav-questions' },
  { icon: <Trophy className="w-5 h-5" />, label: 'Victoires', path: '/victories', color: 'text-yellow-500', tourId: 'nav-victories' },
  { icon: <Telescope className="w-5 h-5" />, label: 'Vigie Immo', path: '/vigie', color: 'text-emerald-500', tourId: 'nav-vigie' },
  { icon: <MapPin className="w-5 h-5" />, label: 'Carte', path: '/members-map', color: 'text-blue-600' },
  { icon: <Activity className="w-5 h-5" />, label: 'Actualite Communaute', path: '/feed', color: 'text-indigo-500' },
  { icon: <Compass className="w-5 h-5" />, label: 'Objectifs', path: '/objectives', color: 'text-purple-500' },
  { icon: <Users className="w-5 h-5" />, label: 'Ambassadeur', path: '/ambassador', color: 'text-green-500' },
  { icon: <Lightbulb className="w-5 h-5" />, label: 'Idees', path: '/ideas', color: 'text-primary' },
  { icon: <Waves className="w-5 h-5" />, label: 'Saisons', path: '/seasons', color: 'text-blue-400' },
  { icon: <FolderOpen className="w-5 h-5" />, label: 'Ressources', path: '/resources', color: 'text-teal-500' },
];

const HomePage = () => {
  const { user, logout, api, isAdmin } = useAuth();
  const { theme, changeTheme, zenMode, toggleZenMode } = useTheme();
  const { triggerNavigation } = useAnimations();
  const { counts: unreadCounts, total: totalUnread } = useUnread();
  const navigate = useNavigate();
  
  const { preferences, toggleSection, resetToDefaults, isSectionVisible, getSectionOrder, updateSectionOrder } = useDashboardPreferences();
  const {
    dashboardData, liveEvent, loading, earnedBadges, unreadNotifications,
    topContributors, showLocationPrompt, handleLocationPromptClose,
  } = useDashboardData();

  const [showAllNav, setShowAllNav] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showWelcomeRecap, setShowWelcomeRecap] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Get section order from preferences - use the hook's value directly
  const sectionOrder = getSectionOrder();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const currentOrder = getSectionOrder();
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      updateSectionOrder(newOrder);
      toast.success('Ordre mis à jour !');
    }
  };

  // Check if we should show welcome recap (after 5 days of absence)
  useEffect(() => {
    if (!user || loading) return;
    
    // Use sessionStorage to track if we already processed this session
    const sessionChecked = sessionStorage.getItem('welcome_recap_checked');
    if (sessionChecked) return;
    
    const lastRecapSeen = localStorage.getItem('last_recap_seen');
    const lastLogin = localStorage.getItem('last_login_time');
    
    // Show recap if it's been more than 5 days since last visit
    const DAYS_THRESHOLD = 5;
    const MS_IN_A_DAY = 1000 * 60 * 60 * 24;
    
    let shouldShowRecap = false;
    
    if (lastLogin) {
      const lastLoginDate = new Date(lastLogin);
      const daysSinceLastLogin = (Date.now() - lastLoginDate.getTime()) / MS_IN_A_DAY;
      
      // Only show if >= 5 days AND recap wasn't shown after last login
      if (daysSinceLastLogin >= DAYS_THRESHOLD) {
        const notRecentlyShown = !lastRecapSeen || new Date(lastRecapSeen) < lastLoginDate;
        if (notRecentlyShown) {
          shouldShowRecap = true;
          setTimeout(() => setShowWelcomeRecap(true), 2000);
        }
      }
    }
    
    // Mark this session as checked
    sessionStorage.setItem('welcome_recap_checked', 'true');
    
    // Update last login time only if we're not showing the recap
    // (if showing recap, we update it when user dismisses the recap)
    if (!shouldShowRecap) {
      localStorage.setItem('last_login_time', new Date().toISOString());
    }
  }, [user, loading]);

  // Global keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleNavigation = (path) => { triggerNavigation(); navigate(path); };

  // Render a section by its ID (for drag and drop reordering)
  const renderSection = (sectionId) => {
    switch (sectionId) {
      case 'progression':
        return !zenMode && (
          <CollapsibleCard id="progression" title="Ta Progression" icon={<Compass className="w-5 h-5 text-primary" />} data-tutorial="progression-card">
            <div className="space-y-3" onClick={() => handleNavigation('/my-stats')}>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Chapitres completes</span>
                <span className="font-semibold">{dashboardData?.progress?.chapters_completed?.length || 0}/{dashboardData?.progress?.total_chapters || 15}</span>
              </div>
              <Progress value={dashboardData?.progress?.percentage || 0} className="h-3" />
              <p className="text-xs text-muted-foreground text-center">{dashboardData?.progress?.percentage || 0}% de la formation completee</p>
            </div>
          </CollapsibleCard>
        );
      
      case 'xpRewards':
        return !zenMode && <XPRewardsCard />;

      case 'odyssey':
        return <OdysseyCard dashboardData={dashboardData} onNavigate={handleNavigation} />;
      
      case 'challenges':
        return !zenMode && <ChallengesCard />;
      
      case 'season':
        return !zenMode && dashboardData?.active_season && (
          <CollapsibleCard id="season" title="Saison en cours" icon={<Waves className="w-5 h-5 text-blue-500" />} className="border-blue-500/20 bg-blue-500/5" mobileDefaultCollapsed>
            <h3 className="font-semibold mb-2">{dashboardData.active_season.name}</h3>
            {dashboardData.season_progress ? (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tes points</span>
                  <span className="font-bold text-blue-500">{dashboardData.season_progress.points_earned || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Objectifs</span>
                  <span>{dashboardData.season_progress.objectives_completed?.length || 0}/{dashboardData.active_season.objectives?.length || 0}</span>
                </div>
              </div>
            ) : (
              <Button variant="outline" className="w-full border-blue-500/30 text-blue-500 hover:bg-blue-500/10" onClick={() => handleNavigation('/seasons')}>
                Rejoindre la saison
              </Button>
            )}
          </CollapsibleCard>
        );
      
      case 'quickNav':
        return (
          <>
            <div className="grid grid-cols-4 gap-3" data-tutorial="quick-nav">
              {NAV_ITEMS.slice(0, showAllNav ? NAV_ITEMS.length : 8).map((item) => {
                const showUnread = item.path === '/channels' && totalUnread > 0;
                return item.showProgress && dashboardData?.odyssee ? (
                  <Button key={item.path} variant="outline" className="h-auto py-3 px-2 flex flex-col items-center gap-1 hover:bg-secondary/50 col-span-1"
                    onClick={() => handleNavigation(item.path)} 
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    data-tour={item.tourId}>
                    <span className={item.color}>{item.icon}</span>
                    <span className="text-xs font-medium">{item.label}</span>
                  </Button>
                ) : (
                  <Button key={item.path} variant="outline" className="relative h-auto py-3 flex flex-col items-center gap-1 hover:bg-secondary/50"
                    onClick={() => handleNavigation(item.path)} 
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    data-tour={item.tourId}>
                    <span className={item.color}>{item.icon}</span>
                    <span className="text-xs">{item.label}</span>
                    {showUnread && (
                      <span
                        className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none flex items-center justify-center animate-pulse-unread"
                        style={{ background: 'hsl(var(--red-dossier))', color: '#fff', boxShadow: '0 0 0 2px hsl(var(--paper))' }}
                        data-testid="unread-badge-channels"
                      >
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
            {NAV_ITEMS.length > 8 && (
              <Button variant="ghost" size="sm" className="w-full text-muted-foreground text-xs mt-2" onClick={() => setShowAllNav(!showAllNav)}>
                {showAllNav ? 'Reduire' : `Voir plus (${NAV_ITEMS.length - 8} autres)`}
              </Button>
            )}
          </>
        );
      
      case 'events':
        return (
          <CollapsibleCard id="events" title="Evenements a venir" icon={<Calendar className="w-5 h-5 text-orange-500" />} mobileDefaultCollapsed
            headerRight={<Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleNavigation('/calendar'); }}>Voir tout <ChevronRight className="w-4 h-4 ml-1" /></Button>}>
            {!dashboardData?.upcoming_events?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun evenement prevu</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.upcoming_events.slice(0, 3).map((event) => (
                  <div key={event.id} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg" data-testid={`event-${event.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{event.title}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(event.start_time)}</p>
                    </div>
                    {event.event_link && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => window.open(event.event_link, '_blank')}><ExternalLink className="w-4 h-4" /></Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        );
      
      case 'calendar':
        return (
          <CollapsibleCard id="calendar" title="Calendrier" icon={<Calendar className="w-5 h-5 text-red-500" />} mobileDefaultCollapsed
            headerRight={<Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleNavigation('/calendar'); }}>Voir tout <ChevronRight className="w-4 h-4 ml-1" /></Button>}>
            <CalendarWidget />
          </CollapsibleCard>
        );
      
      case 'messages':
        return (
          <CollapsibleCard id="messages" title="Messages importants" icon={<Bell className="w-5 h-5 text-blue-500" />} mobileDefaultCollapsed>
            {!dashboardData?.important_messages?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun message important</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.important_messages.slice(0, 3).map((msg) => (
                  <div key={msg.id} className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{msg.author_name}</span>
                      {msg.is_pinned && <Badge variant="outline" className="text-xs">Epingle</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{renderWithMentions(msg.content)}</p>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        );
      
      case 'ideas':
        return (
          <CollapsibleCard id="ideas" title="Idees populaires" icon={<Lightbulb className="w-5 h-5 text-primary" />} mobileDefaultCollapsed
            headerRight={<Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleNavigation('/ideas'); }}>Voir tout <ChevronRight className="w-4 h-4 ml-1" /></Button>}>
            {!dashboardData?.top_ideas?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune idee pour le moment</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.top_ideas.map((idea) => (
                  <div key={idea.id} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-primary">{idea.vote_count || 0}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium line-clamp-1">{idea.title}</p>
                      <p className="text-xs text-muted-foreground">{idea.user_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        );
      
      case 'victories':
        return (
          <CollapsibleCard id="victories" title="Dernieres victoires" icon={<Trophy className="w-5 h-5 text-yellow-500" />} mobileDefaultCollapsed
            headerRight={<Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleNavigation('/victories'); }}>Voir tout <ChevronRight className="w-4 h-4 ml-1" /></Button>}>
            {!dashboardData?.recent_victories?.length ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune victoire recente</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.recent_victories.map((victory) => (
                  <div key={victory.id} className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={victory.user_avatar} />
                        <AvatarFallback className="text-xs bg-yellow-500/20">{victory.user_name?.[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">{victory.user_name}</span>
                    </div>
                    <p className="text-sm line-clamp-2">{victory.title}</p>
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">{victory.congratulations?.length || 0} felicitations</p>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleCard>
        );
      
      case 'leaderboard':
        return !zenMode && <LeaderboardPreview contributors={topContributors} onNavigate={handleNavigation} loading={loading} />;
      
      case 'vigie':
        return !zenMode && <VigieCard />;

      case 'questions':
        return <QuestionsSpotlightCard />;
      
      default:
        return null;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Skeleton loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-6 max-w-7xl space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-muted/50 animate-pulse" />
              <div className="space-y-2">
                <div className="h-5 w-32 bg-muted/50 rounded animate-pulse" />
                <div className="h-3 w-24 bg-muted/50 rounded animate-pulse" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-24 h-9 bg-muted/50 rounded animate-pulse" />
              <div className="w-20 h-9 bg-muted/50 rounded animate-pulse" />
            </div>
          </div>
          
          {/* Profile skeleton */}
          <SkeletonProfile />
          
          {/* Grid skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SkeletonCard lines={4} />
            <SkeletonNavGrid />
            <SkeletonLeaderboard />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SkeletonCard lines={3} />
            <SkeletonCard lines={3} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <div className="noise-overlay" />
      
      <div className="md:hidden">
        <DashboardHeader
          handleNavigation={handleNavigation}
          isAdmin={isAdmin}
          preferences={preferences}
          toggleSection={toggleSection}
          resetToDefaults={resetToDefaults}
          isSectionVisible={isSectionVisible}
          unreadNotifications={unreadNotifications}
          logout={logout}
          onSearchClick={() => setShowSearch(true)}
        />
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 pt-4 pb-8">
        {/* Edit Mode Toggle */}
        <div className="flex justify-end mb-4">
          <Button
            variant={isEditMode ? "default" : "outline"}
            size="sm"
            onClick={() => setIsEditMode(!isEditMode)}
            className={isEditMode ? "bg-primary" : ""}
          >
            <Settings2 className="w-4 h-4 mr-2" />
            {isEditMode ? "Terminer" : "Réorganiser"}
          </Button>
        </div>

        {isEditMode && (
          <div className="mb-4 p-3 bg-primary/10 border border-primary/30 rounded-lg text-sm text-center">
            <GripVertical className="w-4 h-4 inline mr-2" />
            Glissez les sections pour les réorganiser
          </div>
        )}

        {/* Crew Progress Banner ("L'Équipage progresse") */}
        <CrewProgressBanner />

        {/* Stories Section */}
        {isSectionVisible('stories') && <StoriesMoments />}

        {/* Profile Section */}
        <div className="mb-6" data-tutorial="profile-card">
          <Card className="card-base overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-4">
                <div className="cursor-pointer hover-lift" onClick={() => handleNavigation('/profile')}>
                  <FramedAvatar
                    src={user?.avatar_url}
                    fallback={`${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`}
                    frame={user?.profile_frame}
                    role={user?.role}
                    seniority={user?.seniority}
                    size="xl"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-heading font-bold text-lg">{user?.first_name} {user?.last_name}</h2>
                    <Badge className="bg-primary/20 text-primary text-xs">{user?.role?.replace('_', ' ')}</Badge>
                    <FlameIndicator variant="inline" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-primary/80 font-semibold mt-1" data-testid="user-title">
                    {titleForLevel(user?.level || 1)}
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <AnimatedXPCounter value={user?.points || 0} />
                    <span className="text-sm text-muted-foreground">Niveau {user?.level || 1}</span>
                  </div>
                  <AnimatedLevelProgress currentXP={user?.points || 0} level={user?.level || 1} />
                  <div className="mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleNavigation('/profile')}
                      className="border-primary/30 text-primary hover:bg-primary/10"
                      data-testid="my-profile-btn"
                    >
                      <User className="w-4 h-4 mr-1.5" />
                      Mon profil
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Badges obtenus</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigation('/my-stats')}>
                  Mes stats <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {earnedBadges.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">
                    Aucun badge pour l&apos;instant — continue à progresser !
                  </p>
                ) : (
                  earnedBadges.slice(0, 12).map(badge => (
                    <AnimatedBadge key={badge.id} badgeId={badge.id} size="sm" showAnimation={false} />
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Streak Card (only shows when streak >= 7) */}
        <div className="mb-6">
          <FlameIndicator variant="card" />
        </div>

        {/* Daily Challenge - always visible */}
        <DailyChallengeCard />



        {/* Dashboard Content */}
        {isEditMode ? (
          // Edit mode: 2 column sortable layout with drag handles
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sectionOrder.filter(id => isSectionVisible(id) && id !== 'stories' && id !== 'profile')}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                {(() => {
                  const visible = sectionOrder.filter(id => isSectionVisible(id) && id !== 'stories' && id !== 'profile');
                  const col1 = visible.filter((_, i) => i % 2 === 0);
                  const col2 = visible.filter((_, i) => i % 2 !== 0);
                  return (
                    <>
                      <div className="space-y-4">
                        {col1.map(sectionId => (
                          <DraggableSection key={sectionId} id={sectionId} isEditMode={isEditMode}>
                            <div className="p-1">{renderSection(sectionId)}</div>
                          </DraggableSection>
                        ))}
                      </div>
                      <div className="space-y-4">
                        {col2.map(sectionId => (
                          <DraggableSection key={sectionId} id={sectionId} isEditMode={isEditMode}>
                            <div className="p-1">{renderSection(sectionId)}</div>
                          </DraggableSection>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          // Normal mode: 2 column masonry-like layout
          (() => {
            const orderedSections = sectionOrder.filter(id => 
              isSectionVisible(id) && id !== 'stories' && id !== 'profile'
            );
            
            // Distribute sections into 2 columns alternately
            const col1 = [];
            const col2 = [];
            orderedSections.forEach((id, i) => {
              if (i % 2 === 0) col1.push(id);
              else col2.push(id);
            });
            
            return (
              <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
                {/* Left Column */}
                <div className="space-y-5">
                  {col1.map(sectionId => (
                    <div key={sectionId}>{renderSection(sectionId)}</div>
                  ))}
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  {col2.map(sectionId => (
                    <div key={sectionId}>{renderSection(sectionId)}</div>
                  ))}
                  
                  {/* Live Event - always visible when active */}
                  {liveEvent && (
                    <Card className="card-base border-red-500/30 bg-red-500/10 animate-pulse-slow" data-testid="live-event-banner">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <Video className="w-6 h-6 text-red-500" />
                              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-ping" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-red-500">EN DIRECT</p>
                              <p className="text-sm font-medium">{liveEvent.title}</p>
                            </div>
                          </div>
                          {liveEvent.event_link && (
                            <Button size="sm" className="bg-red-500 text-white hover:bg-red-600" onClick={() => window.open(liveEvent.event_link, '_blank')}>
                              Rejoindre
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            );
          })()
        )}
      </main>

      {/* Location Prompt - Affiché seulement après le tutoriel */}
      <Dialog open={showLocationPrompt} onOpenChange={() => handleLocationPromptClose()}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" />Rejoins la carte des membres !</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Indique ta ville pour apparaitre sur la carte de la communaute et decouvrir les membres pres de chez toi.</p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Decouvre les membres de ta region</li>
            <li className="flex items-center gap-2"><Users className="w-4 h-4 text-primary" />Connecte-toi avec des investisseurs locaux</li>
          </ul>
          <p className="text-xs text-muted-foreground">Tu peux toujours modifier ou desactiver cette option dans ton profil.</p>
          <div className="flex gap-3 mt-2">
            <Button variant="outline" className="flex-1" onClick={() => handleLocationPromptClose()}>Plus tard</Button>
            <Button className="flex-1" onClick={() => { handleLocationPromptClose(true); navigate('/members-map'); }}><MapPin className="w-4 h-4 mr-2" />Voir la carte</Button>
          </div>
        </DialogContent>
      </Dialog>

      <PushNotificationManager />
      
      {/* Quick Actions FAB */}
      <QuickActions />      
      {/* Global Search Modal */}
      <GlobalSearch isOpen={showSearch} onClose={() => setShowSearch(false)} />
      
      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* Welcome Back Recap */}
      {showWelcomeRecap && (
        <WelcomeBackRecap onDismiss={() => setShowWelcomeRecap(false)} />
      )}
    </div>
  );
};

export default HomePage;
